import { useState, useRef, useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import {
  Mail,
  Instagram,
  LifeBuoy,
  MessageCircle,
  Send,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";

const channels = [
  {
    icon: Mail,
    bg: "bg-hotpink text-white",
    title: "General & sales",
    detail: "Questions before you connect your store.",
    action: "billing@checkoutdm.com",
    href: "mailto:billing@checkoutdm.com",
  },
  {
    icon: LifeBuoy,
    bg: "bg-mint text-ink",
    title: "Merchant support",
    detail: "Already using Checkout DM and need help?",
    action: "support@checkoutdm.com",
    href: "mailto:support@checkoutdm.com",
  },
  {
    icon: MessageCircle,
    bg: "bg-ink text-white",
    title: "WhatsApp",
    detail: "Want a fast reply? Message us directly.",
    action: "@princejindalai",
    href: "https://wa.me/919463881732?text=Hi%20Checkout%20DM%2C%20I%20have%20a%20question",
  },
  {
    icon: Instagram,
    bg: "bg-peach text-ink",
    title: "Talk to the founder",
    detail: "DM me directly on Instagram — @princejindalai.",
    action: "@princejindalai",
    href: "https://ig.me/m/princejindalai",
  },
];

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const CONTACT_ENDPOINT = process.env.REACT_APP_CONTACT_WEBHOOK_URL;

function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "General question",
    message: "",
    igHandle: "",
    website: "", // honeypot
  });
  const [token, setToken] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [error, setError] = useState("");
  const widgetRef = useRef(null);
  const widgetId = useRef(null);

  useEffect(() => {
    // The widget's container div unmounts when we show the success
    // screen, so the old widget id is stale by the time we return to
    // the form. Drop it so renderWidget() below creates a fresh
    // widget instead of silently doing nothing.
    if (status === "success") {
      widgetId.current = null;
      return;
    }
    if (window.turnstile) {
      renderWidget();
      return;
    }
    if (!document.getElementById("cf-turnstile-script")) {
      const script = document.createElement("script");
      script.id = "cf-turnstile-script";
      script.src =
        "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoadContact";
      script.async = true;
      script.defer = true;
      window.onTurnstileLoadContact = renderWidget;
      document.body.appendChild(script);
    }
  }, [status]);

  function renderWidget() {
    if (!window.turnstile || !widgetRef.current || widgetId.current) return;
    widgetId.current = window.turnstile.render(widgetRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      callback: (t) => setToken(t),
      "expired-callback": () => setToken(""),
      "error-callback": () => setToken(""),
    });
  }

  function handleChange(e) {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    // Honeypot tripped — silently "succeed" without hitting the backend
    if (form.website) {
      setStatus("success");
      return;
    }
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Please fill in your name, email, and message.");
      return;
    }
    if (form.subject === "Merchant support" && !form.igHandle.trim()) {
      setError("Please share your Instagram handle so we can look up your account.");
      return;
    }
    if (!token) {
      setError("Please complete the verification check.");
      return;
    }

    setStatus("submitting");
    try {
      const res = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          subject: form.subject,
          message: form.message.trim(),
          igHandle: form.igHandle.trim(),
          turnstileToken: token,
          website: form.website,
        }),
      });

      if (!res.ok) throw new Error("Request failed");
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Could not send message");

      setStatus("success");
      setForm({ name: "", email: "", subject: "General question", message: "", igHandle: "", website: "" });
      setToken("");
      if (window.turnstile && widgetId.current) window.turnstile.reset(widgetId.current);
    } catch (err) {
      setStatus("error");
      setError("Something went wrong. Please try again or email us directly.");
    }
  }

  if (status === "success") {
    return (
      <div className="flex flex-col items-center rounded-3xl border border-ink/10 bg-white p-10 text-center">
        <CheckCircle2 className="text-mint" size={40} />
        <p className="mt-4 font-display text-xl font-black tracking-tight">Message sent!</p>
        <p className="mt-2 max-w-sm text-sm text-ink/60">
          We'll get back to you within 1 business day.
        </p>
        <button
          onClick={() => setStatus("idle")}
          className="mt-6 text-sm font-semibold text-hotpink hover:underline"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="relative rounded-3xl border border-ink/10 bg-white p-8">
      {/* Honeypot — invisible to real users, most bots fill every field they find */}
      <input
        type="text"
        name="website"
        value={form.website}
        onChange={handleChange}
        autoComplete="off"
        tabIndex={-1}
        className="absolute left-[-9999px] top-0 h-0 w-0 opacity-0"
        aria-hidden="true"
      />

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-ink/50">Name</label>
          <input
            name="name"
            value={form.name}
            onChange={handleChange}
            required
            maxLength={100}
            className="mt-1.5 w-full rounded-xl border border-ink/15 px-4 py-2.5 text-sm outline-none focus:border-hotpink"
            placeholder="Your name"
          />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-ink/50">Email</label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            required
            maxLength={150}
            className="mt-1.5 w-full rounded-xl border border-ink/15 px-4 py-2.5 text-sm outline-none focus:border-hotpink"
            placeholder="you@business.com"
          />
        </div>
      </div>

      <div className="mt-5">
        <label className="text-xs font-bold uppercase tracking-wide text-ink/50">Topic</label>
        <select
          name="subject"
          value={form.subject}
          onChange={handleChange}
          className="mt-1.5 w-full rounded-xl border border-ink/15 px-4 py-2.5 text-sm outline-none focus:border-hotpink"
        >
          <option>General question</option>
          <option>Sales / pricing</option>
          <option>Merchant support</option>
          <option>Partnership</option>
          <option>Something else</option>
        </select>
      </div>

      {form.subject === "Merchant support" && (
        <div className="mt-5">
          <label className="text-xs font-bold uppercase tracking-wide text-ink/50">
            Your Instagram Handle
          </label>
          <input
            name="igHandle"
            value={form.igHandle}
            onChange={handleChange}
            required
            maxLength={60}
            className="mt-1.5 w-full rounded-xl border border-ink/15 px-4 py-2.5 text-sm outline-none focus:border-hotpink"
            placeholder="@yourstorehandle"
          />
          <p className="mt-1.5 text-xs text-ink/50">
            So we can pull up your account details to help you better.
          </p>
        </div>
      )}

      <div className="mt-5">
        <label className="text-xs font-bold uppercase tracking-wide text-ink/50">Message</label>
        <textarea
          name="message"
          value={form.message}
          onChange={handleChange}
          required
          maxLength={2000}
          rows={5}
          className="mt-1.5 w-full rounded-xl border border-ink/15 px-4 py-2.5 text-sm outline-none focus:border-hotpink"
          placeholder="How can we help?"
        />
      </div>

      <div ref={widgetRef} className="mt-5" />

      {error && (
        <p className="mt-4 flex items-center gap-2 text-sm font-medium text-red-600">
          <AlertCircle size={16} /> {error}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-hotpink px-6 py-3 text-sm font-bold text-white transition-opacity disabled:opacity-60"
      >
        <Send size={16} />
        {status === "submitting" ? "Sending..." : "Send message"}
      </button>
    </form>
  );
}

export default function ContactUs() {
  return (
    <>
      <SEO
        title="Contact Us | Checkout DM"
        description="Reach the Checkout DM team for sales, merchant support, or partnership questions — by email, WhatsApp, or the contact form."
        path="/contact"
      />
      <Navbar />

      <section className="px-6 pt-16 pb-10 md:px-12 md:pt-24">
        <div className="mx-auto max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Contact</p>
          <h1 className="mt-3 font-display text-4xl leading-[0.95] tracking-tighter md:text-6xl">
            Let's talk.
          </h1>
          <p className="mt-5 max-w-xl text-lg text-ink/70">
            Whether you're a merchant with a question or a buyer trying to track an order,
            here's how to reach us.
          </p>
        </div>
      </section>

      <section className="px-6 pb-24 md:px-12">
        <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-4">
          {channels.map(({ icon: Icon, bg, title, detail, action, href }) => (
            <a
              key={title}
              href={href}
              target={href.startsWith("http") ? "_blank" : undefined}
              rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
              className="group flex flex-col rounded-[2rem] border border-ink/10 bg-white p-7 transition-shadow hover:shadow-lg"
            >
              <span className={`inline-flex h-10 w-10 items-center justify-center rounded-full ${bg}`}>
                <Icon size={18} />
              </span>
              <p className="mt-5 font-display text-xl font-black tracking-tight">{title}</p>
              <p className="mt-2 text-sm text-ink/60">{detail}</p>
              <p className="mt-4 text-sm font-semibold text-hotpink group-hover:underline">
                {action}
              </p>
            </a>
          ))}
        </div>

        <div className="mx-auto mt-10 max-w-5xl">
          <p className="font-display text-2xl font-black tracking-tight">Or send us a message</p>
          <p className="mt-1 text-sm text-ink/60">We usually reply within a business day.</p>
          <div className="mt-5">
            <ContactForm />
          </div>
        </div>

        <div className="mx-auto mt-10 max-w-5xl rounded-3xl border border-ink/10 bg-white p-8 text-center">
          <p className="font-display text-xl font-black tracking-tight">
            Buying from a merchant on Instagram?
          </p>
          <p className="mx-auto mt-2 max-w-xl text-sm text-ink/60">
            Order and refund questions are handled by the merchant you purchased from — reply
            directly in the DM thread where you checked out.
          </p>
        </div>
      </section>

      <Footer />
    </>
  );
}
